import React, { useState } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import PressHeader from "./PressHeader";

// Import Icons
import {
  FaThLarge, FaMicrophone, FaVideo, FaSignOutAlt, FaDollarSign, FaHistory, FaCog as FaSettings, FaWallet, FaBars, FaTimes, FaPlus, FaRegUserCircle
} from "react-icons/fa";

const PressLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    setIsSidebarOpen(false); // Close sidebar on mobile
    Swal.fire({
      title: "Logout?",
      text: "Are you sure you want to end your session?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
      reverseButtons: true, // Standard mobile UX
      customClass: {
        popup: 'rounded-[2rem] p-4 sm:p-6',
        title: 'text-xl font-black text-slate-800',
        htmlContainer: 'text-sm font-medium text-slate-500',
        confirmButton: 'rounded-xl font-bold px-6 py-2.5',
        cancelButton: 'rounded-xl font-bold px-6 py-2.5 bg-slate-100 text-slate-600 hover:bg-slate-200 border-none'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("pressToken");
        localStorage.removeItem("pressUser");
        localStorage.removeItem("userRole");
        navigate("/press/login");
        toast.success("Successfully logged out"); // Using toast if available, else omit
      }
    });
  };

  const onNavigate = (path) => {
    navigate(`/press/${path}`);
    setIsSidebarOpen(false);
  };

  const menuItems = [
    {
      title: "Main",
      items: [
        { path: "dashboard", icon: FaThLarge, label: "Dashboard", color: "blue" }
      ]
    },
    {
      title: "Create New",
      items: [
        { path: "free-conference", icon: FaMicrophone, label: "Create Free Press Conference", color: "purple" },
        { path: "paid-conference", icon: FaDollarSign, label: "Create Paid Press Conference", color: "amber" }
      ]
    },
    {
      title: "My Conferences",
      items: [
        { path: "conference-status", icon: FaVideo, label: "Free Status", color: "purple" },
        { path: "paid-conference-status", icon: FaHistory, label: "Paid Status", color: "amber" }
      ]
    },
    {
      title: "Account",
      items: [
        { path: "wallet", icon: FaWallet, label: "Wallet", color: "emerald" },
        { path: "history-payment", icon: FaHistory, label: "Payments", color: "indigo" },
        { path: "settings", icon: FaSettings, label: "Settings", color: "slate" }
      ]
    }
  ];

  const getColorClasses = (color, isActive) => {
    const map = {
      blue: { bg: "bg-blue-600 text-white shadow-blue-200", text: "text-blue-500", hoverBg: "hover:bg-blue-50" },
      purple: { bg: "bg-purple-600 text-white shadow-purple-200", text: "text-purple-500", hoverBg: "hover:bg-purple-50" },
      amber: { bg: "bg-amber-500 text-white shadow-amber-200", text: "text-amber-500", hoverBg: "hover:bg-amber-50" },
      emerald: { bg: "bg-emerald-500 text-white shadow-emerald-200", text: "text-emerald-500", hoverBg: "hover:bg-emerald-50" },
      indigo: { bg: "bg-indigo-500 text-white shadow-indigo-200", text: "text-indigo-500", hoverBg: "hover:bg-indigo-50" },
      slate: { bg: "bg-slate-700 text-white shadow-slate-200", text: "text-slate-500", hoverBg: "hover:bg-slate-50" }
    };
    const style = map[color] || map.blue;
    return isActive ? `${style.bg} shadow-md` : `bg-transparent text-slate-600 ${style.hoverBg}`;
  };

  const getIconColorClasses = (color, isActive) => {
    if (isActive) return "text-white";
    const map = {
      blue: "text-blue-500 bg-blue-50",
      purple: "text-purple-500 bg-purple-50",
      amber: "text-amber-500 bg-amber-50",
      emerald: "text-emerald-500 bg-emerald-50",
      indigo: "text-indigo-500 bg-indigo-50",
      slate: "text-slate-500 bg-slate-100"
    };
    return map[color] || map.blue;
  };

  return (
    <div className="flex h-screen bg-[#f4f6fb] overflow-hidden">

      {/* ── Mobile Overlay ── */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar (Hidden on Mobile when closed, Drawer when open) ── */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-100 shadow-2xl lg:shadow-none
        transform transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
        flex flex-col
        ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
        lg:translate-x-0 lg:static lg:flex-shrink-0
      `}>

        {/* Brand Header */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-[0.6rem] bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center flex-shrink-0 shadow-sm">
              <FaMicrophone className="text-white w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-800 leading-tight">IINSAF NEWS</h2>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest leading-none mt-0.5">Press Portal</p>
            </div>
          </div>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="lg:hidden w-8 h-8 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 active:scale-95 transition-all"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Nav Links */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">
          {menuItems.map((section, idx) => (
            <div key={idx}>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-3">
                {section.title}
              </p>
              <div className="space-y-1">
                {section.items.map((item, itemIdx) => {
                  const isActive = location.pathname.includes(`/press/${item.path}`);
                  return (
                    <button
                      key={itemIdx}
                      onClick={() => onNavigate(item.path)}
                      className={`
                        w-full flex items-center text-left gap-3 px-3 py-2.5 rounded-xl text-sm font-black transition-all active:scale-95
                        ${getColorClasses(item.color, isActive)}
                      `}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${getIconColorClasses(item.color, isActive)}`}>
                        <item.icon className="w-4 h-4" />
                      </div>
                      <span className="flex-1 leading-tight">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t border-slate-100 flex-shrink-0">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-black text-rose-600 hover:bg-rose-50 transition-all active:scale-95">
            <div className="w-8 h-8 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
              <FaSignOutAlt className="w-4 h-4 text-rose-500" />
            </div>
            Logout from Press
          </button>
        </div>
      </aside>

      {/* ── Main Content Area ── */}
      <main className="flex-1 flex flex-col min-w-0 bg-[#f4f6fb] overflow-hidden relative">

        {/* Mobile Header Bar */}
        <div className="lg:hidden h-14 bg-white border-b border-slate-100 px-4 flex items-center justify-between flex-shrink-0 z-20 shadow-sm relative">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-600 active:scale-95 transition-all"
          >
            <FaBars className="w-4 h-4" />
          </button>

          <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center">
            <span className="text-sm font-black text-slate-800">IINSAF Press</span>
          </div>

          <button
            onClick={handleLogout}
            className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center text-rose-600 active:scale-95 transition-all"
            title="Logout"
          >
            <FaSignOutAlt className="w-4 h-4" />
          </button>
        </div>

        {/* Desktop Header injected exactly where needed */}
        <div className="hidden lg:block z-20 relative">
          <PressHeader />
        </div>

        {/* Scrollable Page Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar relative pb-[80px] lg:pb-0">
          <div className="h-full">
            <Outlet />
          </div>
        </div>

        {/* ── Mobile Bottom Navigation ── */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 flex items-end justify-between px-2 sm:px-4 pb-safe shadow-[0_-4px_24px_rgba(0,0,0,0.04)] z-50 overflow-visible">

          <div className="flex-1 flex justify-around items-center h-[72px] overflow-x-auto scrollbar-hide py-1">
            <button
              onClick={() => onNavigate('dashboard')}
              className={`flex flex-col items-center justify-center min-w-[3.5rem] h-12 gap-0.5 rounded-xl transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('dashboard') ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <FaThLarge className="w-5 h-5 mb-0.5" />
              <span className="text-[8px] sm:text-[9px] font-black leading-none">Home</span>
            </button>

            <button
              onClick={() => onNavigate('wallet')}
              className={`flex flex-col items-center justify-center min-w-[3.5rem] h-12 gap-0.5 rounded-xl transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('wallet') ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <FaWallet className="w-5 h-5 mb-0.5" />
              <span className="text-[8px] sm:text-[9px] font-black leading-none">Wallet</span>
            </button>

            <button
              onClick={() => onNavigate('conference-status')}
              className={`flex flex-col items-center justify-center min-w-[3.5rem] h-12 gap-0.5 rounded-xl transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('conference-status') ? 'text-purple-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <FaVideo className="w-5 h-5 mb-0.5" />
              <span className="text-[8px] sm:text-[9px] font-black leading-none text-center">Free<br />Status</span>
            </button>
          </div>

          {/* Floating Action Button for Create (Center) */}
          <div className="relative -top-6 mx-1 sm:mx-2 flex-shrink-0 z-50">
            {/* Overlay for closing menu */}
            {showCreateMenu && (
              <div
                className="fixed inset-0 z-40 bg-slate-900/10 backdrop-blur-sm"
                onClick={() => setShowCreateMenu(false)}
              />
            )}

            {/* Pop-up Menu */}
            {showCreateMenu && (
              <div className="absolute bottom-[4.5rem] left-1/2 -translate-x-1/2 w-[85vw] max-w-[280px] bg-white rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.1)] border border-slate-50 p-2 z-50 flex gap-2 transition-all animate-slide-up origin-bottom">
                <button
                  onClick={() => { onNavigate('free-conference'); setShowCreateMenu(false); }}
                  className="flex-1 flex flex-col items-center justify-center gap-2 p-3 pb-4 rounded-[1.25rem] bg-purple-50/80 hover:bg-purple-100 text-purple-700 transition-colors border border-purple-100/50"
                >
                  <div className="w-12 h-12 rounded-[1rem] bg-white flex items-center justify-center shadow-sm border border-purple-100">
                    <FaMicrophone className="w-5 h-5 text-purple-600" />
                  </div>
                  <p className="text-[11px] font-black leading-tight text-center">Create Free<br />Press</p>
                </button>
                <button
                  onClick={() => { onNavigate('paid-conference'); setShowCreateMenu(false); }}
                  className="flex-1 flex flex-col items-center justify-center gap-2 p-3 pb-4 rounded-[1.25rem] bg-amber-50/80 hover:bg-amber-100 text-amber-700 transition-colors border border-amber-100/50"
                >
                  <div className="w-12 h-12 rounded-[1rem] bg-white flex items-center justify-center shadow-sm border border-amber-100">
                    <FaDollarSign className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-[11px] font-black leading-tight text-center">Create Paid<br />Press</p>
                </button>
              </div>
            )}

            <button
              onClick={() => setShowCreateMenu(!showCreateMenu)}
              className={`w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white shadow-xl shadow-blue-500/30 border-4 border-[#f4f6fb] transition-all duration-300 ${showCreateMenu ? "rotate-45" : ""}`}
            >
              <FaPlus className="w-6 h-6 sm:w-7 sm:h-7" />
            </button>
          </div>

          <div className="flex-1 flex justify-around items-center h-[72px] overflow-x-auto scrollbar-hide py-1">
            <button
              onClick={() => onNavigate('paid-conference-status')}
              className={`flex flex-col items-center justify-center min-w-[3.5rem] h-12 gap-0.5 rounded-xl transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('paid-conference-status') ? 'text-amber-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <FaHistory className="w-5 h-5 mb-0.5" />
              <span className="text-[8px] sm:text-[9px] font-black leading-none text-center">Paid<br />Status</span>
            </button>

            <button
              onClick={() => onNavigate('history-payment')}
              className={`flex flex-col items-center justify-center min-w-[3.5rem] h-12 gap-0.5 rounded-xl transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('history-payment') ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <FaDollarSign className="w-5 h-5 mb-0.5" />
              <span className="text-[8px] sm:text-[9px] font-black leading-none">Payments</span>
            </button>

            <button
              onClick={() => onNavigate('settings')}
              className={`flex flex-col items-center justify-center min-w-[3.5rem] h-12 gap-0.5 rounded-xl transition-all active:scale-95 flex-shrink-0 ${location.pathname.includes('settings') ? 'text-slate-800' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <FaRegUserCircle className="w-5 h-5 mb-0.5" />
              <span className="text-[8px] sm:text-[9px] font-black leading-none">Profile</span>
            </button>
          </div>

        </div>

      </main>

    </div>
  );
};

export default PressLayout;
